
import React from 'react';
import { motion } from 'framer-motion';
import { WHATSAPP_URL } from '../constants';
import { ArrowRight } from 'lucide-react';

const Hero: React.FC = () => {
  const titleLetters = "AVUS".split("");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.4,
      },
    },
  };

  const letterVariants = {
    hidden: { opacity: 0, y: -80, filter: 'blur(10px)' },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 120,
      },
    },
  };

  const dotVariants = {
    hidden: { opacity: 0, y: -100, scale: 0.5 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 10,
        stiffness: 150,
        delay: 1.0,
      },
    },
  };

  return (
    <section className="relative min-h-[90vh] md:min-h-screen flex items-center pt-24 md:pt-12 overflow-hidden bg-white">
      {/* Background elements */}
      <div className="absolute inset-0 z-0">
        <motion.div 
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0"
        >
          {/* Subtle geometric background for mobile */}
          <div className="absolute top-0 right-0 w-full md:w-1/2 h-full bg-slate-50/50 md:bg-slate-50 diagonal-bg md:opacity-100 opacity-50"></div>
          
          {/* Main Hero Image with Responsive Tuning */}
          <img 
            src="https://imgpx.com/en/tk9sH50H6F3b.png" 
            alt="AVUS Business Environment"
            className="absolute right-0 top-0 w-full md:w-1/2 h-full object-cover object-right md:object-center opacity-[0.18] md:opacity-80 mix-blend-multiply"
          />
          
          {/* Gradient mask for mobile readability - fades image on the left side */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 md:via-transparent to-transparent z-1"></div>
        </motion.div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-8 md:py-12">
        <div className="w-full md:w-3/5">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-avus-navy text-white px-3 py-1 rounded-full text-[8px] md:text-[9px] font-bold uppercase tracking-[0.2em] mb-6 md:mb-8"
          >
            <span className="text-avus-accent">●</span>
            <span>Licensed Business Support · Qatar</span>
          </motion.div>
          
          <div className="mb-2">
            <motion.h1 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="text-[5.5rem] sm:text-[7rem] md:text-[9.5rem] font-black text-avus-navy leading-[0.85] tracking-tighter flex items-baseline select-none"
            >
              {titleLetters.map((char, index) => (
                <motion.span key={index} variants={letterVariants} className="inline-block">
                  {char}
                </motion.span>
              ))}
              <motion.span variants={dotVariants} className="text-avus-blue inline-block ml-0.5">.</motion.span>
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="mt-2 md:mt-4"
            >
              <h2 className="text-xl md:text-4xl font-semibold text-slate-800 tracking-tight leading-tight">
                Business Services LLC
              </h2>
              <div className="w-12 md:w-20 h-[2px] bg-avus-blue mt-3 mb-3"></div>
              <p className="text-[9px] md:text-xs font-bold uppercase tracking-[0.3em] text-slate-400">
                Business · Support · Advisory
              </p>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="mb-8 md:mb-10 mt-6 md:mt-8"
          >
            <p className="text-base md:text-xl text-slate-500 leading-relaxed max-w-lg font-light">
              Simplifying <span className="font-bold text-avus-navy">Operations</span> & Strengthening <span className="font-bold text-avus-navy">Governance</span> through licensed professional support.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7 }}
            className="flex flex-col sm:flex-row gap-3 md:gap-4"
          >
            <a 
              href="#services" 
              className="bg-avus-navy text-white px-6 md:px-8 py-3.5 md:py-4 rounded-full text-sm font-bold flex items-center justify-center hover:bg-avus-blue transition-all shadow-xl hover:shadow-avus-blue/10 transform hover:-translate-y-1 group"
            >
              Our Solutions
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </a>
            <a 
              href={WHATSAPP_URL} 
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-avus-navy border border-slate-200 px-6 md:px-8 py-3.5 md:py-4 rounded-full text-sm font-bold flex items-center justify-center hover:bg-slate-50 transition-all shadow-sm"
            >
              Consult Now
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
